// src/modules/Admin/pages/ExpenseDeclarations/index.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { supabase } from '../../../../config/supabase';
import { useAuth } from '../../../../contexts/AuthContext';
import {
  FaReceipt,
  FaPlus,
  FaTimes,
  FaEdit,
  FaTrash,
  FaSpinner,
  FaArrowUp,
  FaArrowDown
} from 'react-icons/fa';

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #111;
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #111;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #333;
  }
`;

const SummaryCard = styled.div`
  background: #111;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  color: white;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const SummaryItem = styled.div`
  text-align: center;

  .label {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.6);
    text-transform: uppercase;
    letter-spacing: 0.3px;
    margin-bottom: 4px;
  }

  .value {
    font-size: 20px;
    font-weight: 700;
  }

  .variance {
    font-size: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    margin-top: 4px;
    color: ${props => props.$positive ? '#10b981' : '#ef4444'};
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

const ExpenseItem = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  align-items: center;
  gap: 16px;

  &:last-child {
    border-bottom: none;
  }
`;

const ExpenseInfo = styled.div`
  flex: 1;
`;

const ExpenseCategory = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;

  .category {
    font-size: 14px;
    font-weight: 600;
    color: #111;
  }

  .badge {
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    background: ${props => {
      switch (props.$category) {
        case 'marketing': return 'rgba(59, 130, 246, 0.1)';
        case 'development': return 'rgba(139, 92, 246, 0.1)';
        case 'personnel': return 'rgba(236, 72, 153, 0.1)';
        default: return 'rgba(107, 114, 128, 0.1)';
      }
    }};
    color: ${props => {
      switch (props.$category) {
        case 'marketing': return '#3b82f6';
        case 'development': return '#8b5cf6';
        case 'personnel': return '#ec4899';
        default: return '#6b7280';
      }
    }};
  }
`;

const ExpenseDescription = styled.p`
  font-size: 13px;
  color: #666;
`;

const ExpenseValues = styled.div`
  text-align: right;
  min-width: 140px;

  .actual {
    font-size: 16px;
    font-weight: 600;
    color: #111;
  }

  .planned {
    font-size: 12px;
    color: #999;
  }

  .variance {
    font-size: 11px;
    font-weight: 500;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;
    color: ${props => props.$variance > 0 ? '#ef4444' : '#10b981'};
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
`;

const IconButton = styled.button`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 6px;
  background: ${props => props.$danger ? 'rgba(239, 68, 68, 0.1)' : '#f5f5f5'};
  color: ${props => props.$danger ? '#ef4444' : '#666'};
  cursor: pointer;

  &:hover {
    background: ${props => props.$danger ? 'rgba(239, 68, 68, 0.2)' : '#e5e5e5'};
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #f0f0f0;
`;

const ModalTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #111;
`;

const ModalClose = styled.button`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 8px;
  background: #f5f5f5;
  color: #666;
  cursor: pointer;

  &:hover {
    background: #e5e5e5;
  }
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;

  label {
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: #333;
    margin-bottom: 8px;
  }

  input, select, textarea {
    width: 100%;
    padding: 12px 14px;
    border: 1px solid #e5e5e5;
    border-radius: 8px;
    font-size: 14px;

    &:focus {
      outline: none;
      border-color: #111;
    }
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid #f0f0f0;
`;

const CancelButton = styled.button`
  padding: 10px 20px;
  background: white;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  font-size: 14px;
  color: #666;
  cursor: pointer;

  &:hover {
    background: #f5f5f5;
  }
`;

const SubmitButton = styled.button`
  padding: 10px 24px;
  background: #111;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  color: white;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #333;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #999;
`;

const CATEGORIES = [
  { value: 'marketing', label: 'Marketing' },
  { value: 'development', label: 'Desenvolvimento' },
  { value: 'operations', label: 'Operacoes' },
  { value: 'equipment', label: 'Equipamentos' },
  { value: 'personnel', label: 'Pessoal' },
  { value: 'infrastructure', label: 'Infraestrutura' },
  { value: 'other', label: 'Outros' }
];

const STATUS_OPTIONS = [
  { value: 'planned', label: 'Planejado' },
  { value: 'in_progress', label: 'Em Andamento' },
  { value: 'completed', label: 'Concluido' },
  { value: 'cancelled', label: 'Cancelado' }
];

const ExpenseDeclarations = () => {
  const { user } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [totals, setTotals] = useState({ planned: 0, actual: 0 });
  const [formData, setFormData] = useState({
    category: 'marketing',
    subcategory: '',
    description: '',
    planned_amount: '',
    actual_amount: '0',
    period_type: 'quarterly',
    period_start: new Date().toISOString().split('T')[0],
    period_end: '',
    status: 'planned',
    notes: ''
  });

  useEffect(() => {
    fetchExpenses();
  }, []);

  const fetchExpenses = async () => {
    try {
      const { data, error } = await supabase
        .from('expense_declarations')
        .select('*')
        .order('category', { ascending: true });

      if (!error) {
        setExpenses(data || []);

        const sums = (data || []).reduce((acc, exp) => ({
          planned: acc.planned + (parseFloat(exp.planned_amount) || 0),
          actual: acc.actual + (parseFloat(exp.actual_amount) || 0)
        }), { planned: 0, actual: 0 });

        setTotals(sums);
      }
    } catch (error) {
      console.warn('Error fetching expenses:', error);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingExpense(null);
    const now = new Date();
    const quarterEnd = new Date(now.getFullYear(), Math.ceil((now.getMonth() + 1) / 3) * 3, 0);
    setFormData({
      category: 'marketing',
      subcategory: '',
      description: '',
      planned_amount: '',
      actual_amount: '0',
      period_type: 'quarterly',
      period_start: now.toISOString().split('T')[0],
      period_end: quarterEnd.toISOString().split('T')[0],
      status: 'planned',
      notes: ''
    });
    setShowModal(true);
  };

  const openEditModal = (expense) => {
    setEditingExpense(expense);
    setFormData({
      category: expense.category || 'marketing',
      subcategory: expense.subcategory || '',
      description: expense.description || '',
      planned_amount: expense.planned_amount?.toString() || '',
      actual_amount: expense.actual_amount?.toString() || '0',
      period_type: expense.period_type || 'quarterly',
      period_start: expense.period_start || '',
      period_end: expense.period_end || '',
      status: expense.status || 'planned',
      notes: expense.notes || ''
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.description || !formData.planned_amount) {
      alert('Descricao e valor planejado sao obrigatorios');
      return;
    }

    setSaving(true);
    try {
      const expenseData = {
        ...formData,
        planned_amount: parseFloat(formData.planned_amount) || 0,
        actual_amount: parseFloat(formData.actual_amount) || 0,
        created_by: user?.email || 'admin'
      };

      if (editingExpense) {
        const { error } = await supabase
          .from('expense_declarations')
          .update(expenseData)
          .eq('id', editingExpense.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('expense_declarations')
          .insert([expenseData]);

        if (error) throw error;
      }

      await fetchExpenses();
      setShowModal(false);
    } catch (error) {
      console.error('Error saving expense:', error);
      alert('Erro ao salvar despesa: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (expenseId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta despesa?')) return;

    try {
      const { error } = await supabase
        .from('expense_declarations')
        .delete()
        .eq('id', expenseId);

      if (error) throw error;
      await fetchExpenses();
    } catch (error) {
      console.error('Error deleting expense:', error);
      alert('Erro ao excluir despesa');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0
    }).format(value || 0);
  };

  if (loading) {
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: 60 }}>
          <FaSpinner style={{ fontSize: 24, animation: 'spin 1s linear infinite' }} />
        </div>
      </Container>
    );
  }

  const variance = totals.actual - totals.planned;
  const variancePercent = totals.planned > 0 ? (variance / totals.planned) * 100 : 0;

  return (
    <Container>
      <Header>
        <Title>Declaracao de Gastos</Title>
        <AddButton onClick={openCreateModal}>
          <FaPlus />
          Nova Despesa
        </AddButton>
      </Header>

      <SummaryCard>
        <SummaryItem>
          <div className="label">Planejado</div>
          <div className="value">{formatCurrency(totals.planned)}</div>
        </SummaryItem>
        <SummaryItem>
          <div className="label">Realizado</div>
          <div className="value">{formatCurrency(totals.actual)}</div>
        </SummaryItem>
        <SummaryItem $positive={variance <= 0}>
          <div className="label">Variacao</div>
          <div className="value">{formatCurrency(variance)}</div>
          <div className="variance">
            {variance <= 0 ? <FaArrowDown /> : <FaArrowUp />}
            {Math.abs(variancePercent).toFixed(0)}%
          </div>
        </SummaryItem>
        <SummaryItem>
          <div className="label">Execucao</div>
          <div className="value">{totals.planned > 0 ? ((totals.actual / totals.planned) * 100).toFixed(0) : 0}%</div>
        </SummaryItem>
      </SummaryCard>

      <Card>
        {expenses.length === 0 ? (
          <EmptyState>
            <FaReceipt style={{ fontSize: 40, marginBottom: 16 }} />
            <p>Nenhuma despesa cadastrada</p>
          </EmptyState>
        ) : (
          expenses.map((expense) => {
            const expVariance = (parseFloat(expense.actual_amount) || 0) - (parseFloat(expense.planned_amount) || 0);
            return (
              <ExpenseItem key={expense.id}>
                <ExpenseInfo>
                  <ExpenseCategory $category={expense.category}>
                    <span className="category">{CATEGORIES.find(c => c.value === expense.category)?.label || expense.category}</span>
                    {expense.subcategory && <span className="badge">{expense.subcategory}</span>}
                  </ExpenseCategory>
                  <ExpenseDescription>{expense.description}</ExpenseDescription>
                </ExpenseInfo>
                <ExpenseValues $variance={expVariance}>
                  <div className="actual">{formatCurrency(expense.actual_amount)}</div>
                  <div className="planned">de {formatCurrency(expense.planned_amount)}</div>
                  {expVariance !== 0 && (
                    <div className="variance">
                      {expVariance > 0 ? <FaArrowUp /> : <FaArrowDown />}
                      {formatCurrency(Math.abs(expVariance))}
                    </div>
                  )}
                </ExpenseValues>
                <Actions>
                  <IconButton onClick={() => openEditModal(expense)}>
                    <FaEdit />
                  </IconButton>
                  <IconButton $danger onClick={() => handleDelete(expense.id)}>
                    <FaTrash />
                  </IconButton>
                </Actions>
              </ExpenseItem>
            );
          })
        )}
      </Card>

      {showModal && (
        <ModalOverlay onClick={() => setShowModal(false)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{editingExpense ? 'Editar Despesa' : 'Nova Despesa'}</ModalTitle>
              <ModalClose onClick={() => setShowModal(false)}>
                <FaTimes />
              </ModalClose>
            </ModalHeader>
            <form onSubmit={handleSubmit}>
              <ModalBody>
                <FormRow>
                  <FormGroup>
                    <label>Categoria</label>
                    <select
                      value={formData.category}
                      onChange={e => setFormData({ ...formData, category: e.target.value })}
                    >
                      {CATEGORIES.map(cat => (
                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                      ))}
                    </select>
                  </FormGroup>
                  <FormGroup>
                    <label>Subcategoria</label>
                    <input
                      type="text"
                      value={formData.subcategory}
                      onChange={e => setFormData({ ...formData, subcategory: e.target.value })}
                      placeholder="Ex: Digital, Eventos..."
                    />
                  </FormGroup>
                </FormRow>

                <FormGroup>
                  <label>Descricao *</label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Descricao da despesa"
                    required
                  />
                </FormGroup>

                <FormRow>
                  <FormGroup>
                    <label>Valor Planejado *</label>
                    <input
                      type="number"
                      value={formData.planned_amount}
                      onChange={e => setFormData({ ...formData, planned_amount: e.target.value })}
                      placeholder="15000"
                      required
                    />
                  </FormGroup>
                  <FormGroup>
                    <label>Valor Realizado</label>
                    <input
                      type="number"
                      value={formData.actual_amount}
                      onChange={e => setFormData({ ...formData, actual_amount: e.target.value })}
                      placeholder="0"
                    />
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <FormGroup>
                    <label>Data Inicio</label>
                    <input
                      type="date"
                      value={formData.period_start}
                      onChange={e => setFormData({ ...formData, period_start: e.target.value })}
                    />
                  </FormGroup>
                  <FormGroup>
                    <label>Data Fim</label>
                    <input
                      type="date"
                      value={formData.period_end}
                      onChange={e => setFormData({ ...formData, period_end: e.target.value })}
                    />
                  </FormGroup>
                </FormRow>

                <FormGroup>
                  <label>Status</label>
                  <select
                    value={formData.status}
                    onChange={e => setFormData({ ...formData, status: e.target.value })}
                  >
                    {STATUS_OPTIONS.map(status => (
                      <option key={status.value} value={status.value}>{status.label}</option>
                    ))}
                  </select>
                </FormGroup>

                <FormGroup>
                  <label>Observacoes</label>
                  <textarea
                    value={formData.notes}
                    onChange={e => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Notas adicionais..."
                    rows={2}
                  />
                </FormGroup>
              </ModalBody>
              <ModalFooter>
                <CancelButton type="button" onClick={() => setShowModal(false)}>
                  Cancelar
                </CancelButton>
                <SubmitButton type="submit" disabled={saving}>
                  {saving ? 'Salvando...' : (editingExpense ? 'Atualizar' : 'Criar Despesa')}
                </SubmitButton>
              </ModalFooter>
            </form>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default ExpenseDeclarations;
